import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buscarEquipesPorCategoriaTorneio } from '../../services/equipeService';
import { fetchFasesGrupoPartidasByFaseCategoriaTorneioId } from '../../services/faseGrupoPartidasService';
import type { Equipe as EquipeCategoria } from '../../types/equipe';
import type { FaseGrupoPartidas as FaseGrupoPartidasType } from '../../types/graphql';

export type FaseGrupoEquipeModalProps = {
  open: boolean;
  onClose: () => void;
  faseGrupoPartidasId: string | null;
  faseCategoriaTorneioId: string;
  categoriaTorneioId: string;
  onConfirm: (equipeId: string) => Promise<void>;
};

type GrupoInfo = { grupoId: string; grupoNome: string };

type GrupoLookup = Map<string, GrupoInfo>;

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

const FaseGrupoEquipeModal: React.FC<FaseGrupoEquipeModalProps> = ({
  open,
  onClose,
  faseGrupoPartidasId,
  faseCategoriaTorneioId,
  categoriaTorneioId,
  onConfirm,
}) => {
  const [equipesCategoria, setEquipesCategoria] = useState<EquipeCategoria[]>([]);
  const [equipeIdParaGrupo, setEquipeIdParaGrupo] = useState<GrupoLookup>(new Map());
  const [loadingEquipes, setLoadingEquipes] = useState(false);
  const [loadingMapa, setLoadingMapa] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchEquipes = useCallback(async () => {
    setLoadingEquipes(true);
    try {
      const data = await buscarEquipesPorCategoriaTorneio(categoriaTorneioId);
      if (!isMountedRef.current) {
        return;
      }
      setEquipesCategoria(data);
    } catch (err) {
      if (isMountedRef.current) {
        const message = err instanceof Error ? err.message : 'Nao foi possivel carregar as equipes.';
        setError(message);
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoadingEquipes(false);
      }
    }
  }, [categoriaTorneioId]);

  const buildGrupoLookup = (fases: FaseGrupoPartidasType[]): GrupoLookup => {
    const lookup: GrupoLookup = new Map();
    fases.forEach((grupo) => {
      const grupoNome = grupo.nome ?? '';
      (grupo.equipes ?? []).forEach((equipe) => {
        const equipeId = equipe?.id;
        if (equipeId) {
          lookup.set(equipeId, { grupoId: grupo.id, grupoNome });
        }
      });
    });
    return lookup;
  };

  const fetchMapaGrupos = useCallback(async () => {
    setLoadingMapa(true);
    try {
      const fases = await fetchFasesGrupoPartidasByFaseCategoriaTorneioId(faseCategoriaTorneioId);
      if (!isMountedRef.current) {
        return;
      }
      setEquipeIdParaGrupo(buildGrupoLookup(fases));
    } catch (err) {
      if (isMountedRef.current) {
        const message = err instanceof Error ? err.message : 'Nao foi possivel carregar os grupos.';
        setError((prev) => prev ?? message);
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoadingMapa(false);
      }
    }
  }, [faseCategoriaTorneioId]);

  const loadData = useCallback(async () => {
    setInitialized(false);
    setError(null);
    try {
      await Promise.all([fetchEquipes(), fetchMapaGrupos()]);
    } catch (err) {
      // erros já tratados individualmente
    } finally {
      if (isMountedRef.current) {
        setInitialized(true);
      }
    }
  }, [fetchEquipes, fetchMapaGrupos]);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setAddingId(null);
      setError(null);
      document.body.classList.add('modal-open');
      loadData().catch(() => undefined);
      requestAnimationFrame(() => {
        modalRef.current?.focus();
        inputRef.current?.focus();
      });
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [open, loadData]);

  const normalizedSearch = useMemo(() => normalizeText(searchTerm.trim()), [searchTerm]);

  const filteredEquipes = useMemo(() => {
    if (!normalizedSearch) {
      return equipesCategoria;
    }

    return equipesCategoria.filter((equipe) => {
      const atletas = equipe?.atletas || [];
      return atletas.some((atleta) => {
        const nome = atleta?.usuario?.nome ?? '';
        return normalizeText(nome).includes(normalizedSearch);
      });
    });
  }, [equipesCategoria, normalizedSearch]);

  if (!open) {
    return null;
  }

  const handleClose = () => {
    if (addingId) {
      return;
    }
    onClose();
  };

  const handleAdicionar = async (equipeId: string) => {
    if (!faseGrupoPartidasId) {
      setError('Grupo invalido.');
      return;
    }

    try {
      setAddingId(equipeId);
      setError(null);
      await onConfirm(equipeId);
      await fetchMapaGrupos();
    } catch (err) {
      if (isMountedRef.current) {
        const message = err instanceof Error ? err.message : 'Nao foi possivel adicionar a equipe.';
        setError(message);
      }
    } finally {
      if (isMountedRef.current) {
        setAddingId(null);
      }
    }
  };

  const showInitialLoading = !initialized && (loadingEquipes || loadingMapa);
  const noEquipesCategoria = initialized && equipesCategoria.length === 0;
  const noResultadosFiltro = initialized && equipesCategoria.length > 0 && filteredEquipes.length === 0;

  const renderAtletasPreview = (atletas: EquipeCategoria['atletas']) => {
    if (!atletas || atletas.length === 0) {
      return (
        <div className="text-muted" style={{ fontSize: 12 }}>
          Nenhum atleta vinculado
        </div>
      );
    }

    const preview = atletas.slice(0, 3);
    const restantes = Math.max(0, atletas.length - preview.length);

    return (
      <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
        {preview.map((atleta, index) => (
          <div key={atleta?.id ?? `atleta-${index}`} className="d-flex align-items-center gap-2">
            <img
              src={atleta?.usuario?.imagemUrl || '/assets/images/user.png'}
              alt={atleta?.usuario?.nome || 'Atleta'}
              className="rounded-circle"
              width={36}
              height={36}
              style={{ objectFit: 'cover' }}
            />
            <span style={{ fontSize: 13 }}>{atleta?.usuario?.nome ?? 'Sem nome'}</span>
          </div>
        ))}
        {restantes > 0 && (
          <span className="badge bg-light text-muted">+{restantes}</span>
        )}
      </div>
    );
  };

  const renderEquipeCategoriaItem = (equipe: EquipeCategoria) => {
    const grupoInfo = equipeIdParaGrupo.get(equipe.id);
    const botaoDesabilitado = Boolean(grupoInfo) || !faseGrupoPartidasId || loadingMapa || addingId === equipe.id;
    const tooltipGrupo = grupoInfo
      ? `Ja esta no grupo ${grupoInfo.grupoNome || ''}`.trim() || 'Ja esta em um grupo desta fase'
      : undefined;

    return (
      <div
        key={equipe.id}
        id={`equipe-${equipe.id}`}
        className="border rounded p-3 d-flex flex-column flex-lg-row gap-3"
        data-testid={`equipe-categoria-item-${equipe.id}`}
      >
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 mb-1">
            {grupoInfo && (
              <span className="badge bg-warning text-dark">
                {grupoInfo.grupoId === faseGrupoPartidasId ? 'Neste grupo' : `No grupo: ${grupoInfo.grupoNome || 'Sem nome'}`}
              </span>
            )}
          </div>
          {renderAtletasPreview(equipe.atletas)}
        </div>
        <div className="d-flex align-items-start align-items-lg-end justify-content-lg-end w-100 w-lg-auto">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => handleAdicionar(equipe.id)}
            disabled={botaoDesabilitado}
            data-testid={`btn-adicionar-equipe-${equipe.id}`}
            aria-label="Adicionar equipe ao grupo"
            title={tooltipGrupo}
          >
            {addingId === equipe.id ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              'Adicionar ao grupo'
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        data-testid="modal-add-equipe"
        ref={modalRef}
        aria-modal="true"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Adicionar equipe ao grupo</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={handleClose}
                disabled={Boolean(addingId)}
              />
            </div>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="input-busca-atleta" className="form-label">
                  Buscar por atleta
                </label>
                <input
                  id="input-busca-atleta"
                  ref={inputRef}
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome de um atleta"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  disabled={showInitialLoading}
                  data-testid="input-busca-atleta"
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Equipes inscritas nesta categoria</h6>
                {loadingMapa && initialized && (
                  <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
                )}
              </div>

              {showInitialLoading ? (
                <div className="placeholder-glow" data-testid="modal-equipes-loading">
                  <p className="placeholder col-8 mb-2" />
                  <p className="placeholder col-6" />
                </div>
              ) : (
                <div className="d-flex flex-column gap-3" data-testid="lista-equipes-categoria">
                  {noEquipesCategoria ? (
                    <div className="text-muted" data-testid="modal-equipes-empty">
                      Nenhuma equipe inscrita nesta categoria.
                    </div>
                  ) : noResultadosFiltro ? (
                    <div className="text-muted" data-testid="modal-equipes-empty">
                      Nenhuma equipe encontrada para este termo.
                    </div>
                  ) : (
                    filteredEquipes.map(renderEquipeCategoriaItem)
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={Boolean(addingId)}
                data-testid="btn-modal-cancelar"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FaseGrupoEquipeModal;
